import {
  EMPTY,
  expand,
  from,
  map,
  mergeMap,
  Observable,
  of,
  reduce,
  toArray,
} from "rxjs";
import {
  EnrichmentListPriceItem,
  EnrichmentPriceListResponse,
} from "@app/shared/models/enrichment-detail.model";
import {
  EnrichedSearchProductOfferingParams,
  ProductOfferingService,
} from "../../../services/enrichment-data.service";
import { toPriceRows } from "./enrichment-list.transformer";
import { EnrichmentPriceRow } from "./enrichment-list.types";

const BFF_MAX_PAGE_SIZE = 100;
const MAX_PRICE_IDS_PER_URL = 50;
const READ_CONCURRENCY = 4;

type PriceRowsView = Pick<EnrichedSearchProductOfferingParams, "deleted">;

/** Every list row of `priceIds` on `channel`, one per package that lists the
 *  price. The priceId filter is the only one that narrows the read. The BFF
 *  leaves deleted packages out unless `view.deleted` asks for them. */
export function readPriceRows(
  productOfferingService: ProductOfferingService,
  priceIds: readonly string[],
  channel: string,
  view: PriceRowsView = {},
): Observable<EnrichmentPriceRow[]> {
  const ids = [...new Set(priceIds)].filter(Boolean);
  if (ids.length === 0) return of([]);
  const groups: string[][] = [];
  for (let i = 0; i < ids.length; i += MAX_PRICE_IDS_PER_URL) {
    groups.push(ids.slice(i, i + MAX_PRICE_IDS_PER_URL));
  }
  return from(groups).pipe(
    mergeMap(
      (group) => readEveryPage(productOfferingService, group, channel, view),
      READ_CONCURRENCY,
    ),
    toArray(),
    map((items) =>
      toPriceRows(items.flat(), { channelSelected: true, now: Date.now() }),
    ),
  );
}

/** A price may be listed under many packages, so one read can span pages.
 *  Id order keeps a concurrent write from moving a row across a page. */
function readEveryPage(
  productOfferingService: ProductOfferingService,
  priceIds: readonly string[],
  channel: string,
  view: PriceRowsView,
): Observable<EnrichmentListPriceItem[]> {
  const read = (page: number) =>
    productOfferingService
      .getEnrichmentPrices(
        {
          ...view,
          priceId: priceIds.join(","),
          page,
          pageSize: BFF_MAX_PAGE_SIZE,
          sortBy: "id",
          sortDir: "asc",
        },
        channel,
      )
      .pipe(map((response) => ({ page, items: itemsOf(response), response })));
  return read(1).pipe(
    expand(({ page, items, response }) =>
      items.length > 0 &&
      page * BFF_MAX_PAGE_SIZE < (response.pagination?.totalItems ?? 0)
        ? read(page + 1)
        : EMPTY,
    ),
    reduce(
      (all, { items }) => [...all, ...items],
      [] as EnrichmentListPriceItem[],
    ),
  );
}

function itemsOf(
  response: EnrichmentPriceListResponse,
): EnrichmentListPriceItem[] {
  if (!Array.isArray(response.data)) {
    throw new Error("The price list answered without rows");
  }
  return response.data;
}
